import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MessageBar, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { useQueryClient } from '@tanstack/react-query';

import { TenantDetail, refreshTenant } from '../api/tenantRegistry';
import ConsentStatusChip from '../components/ConsentStatusChip';
import { useEnvironment } from '../context/EnvironmentContext';
import { errorText } from '../lib/errorText';
import { GRANT_RETURN_PATH, decodeGrantState, describeSignInError } from '../lib/tenantConnectRedirect';
import { TEXT } from '../theme/tokens';

// R-FD-055 / FD-566 — where "Grant permissions" returns.
//
// The grant was an /authorize request with .default + prompt=consent against the
// tenant's OWN application (FD-ADR-046 §3 as amended; FD-559). Microsoft sends back
// either an `error`, or a `code` that belongs to a CONFIDENTIAL application — one
// this browser cannot redeem and the console never tries to. The code is stripped
// from the address bar on arrival and ignored.
//
// What this page DOES is the thing the operator came back for: it re-checks the
// tenant at once, so the verdict on screen is the post-grant one rather than an hour
// old. The grant itself is Entra's act; this page only reports what it now sees.

type Outcome =
  | { kind: 'working' }
  | { kind: 'checked'; tenant: TenantDetail }
  | { kind: 'microsoft-error'; message: string }
  | { kind: 'refresh-failed'; error: unknown }
  | { kind: 'unknown-tenant' };

export default function TenantConsentedPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { environmentId, environments, setEnvironmentId, isSingleEnvironment } = useEnvironment();

  const [captured] = useState(() => ({
    grant: decodeGrantState(params.get('state')),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  }));
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'working' });
  const started = useRef(false);

  const wantEnv = captured.grant?.environmentId;
  const envKnown = wantEnv ? environments.some((e) => e.id === wantEnv) : false;
  const envReady = !wantEnv || !envKnown || wantEnv === environmentId;

  useEffect(() => {
    if (wantEnv && envKnown && wantEnv !== environmentId) setEnvironmentId(wantEnv);
  }, [wantEnv, envKnown, environmentId, setEnvironmentId]);

  useEffect(() => {
    if (!envReady || started.current) return;
    started.current = true;

    navigate(
      isSingleEnvironment ? GRANT_RETURN_PATH : `${GRANT_RETURN_PATH}?env=${encodeURIComponent(environmentId)}`,
      { replace: true },
    );

    if (!captured.grant || (wantEnv && !envKnown)) {
      setOutcome({ kind: 'unknown-tenant' });
      return;
    }
    if (captured.error) {
      setOutcome({ kind: 'microsoft-error', message: describeSignInError(captured.error, captured.errorDescription) });
      return;
    }
    const tenantId = captured.grant.tenantId;
    void (async () => {
      try {
        const tenant = await refreshTenant(tenantId);
        queryClient.setQueryData(['tenant', tenantId], tenant);
        queryClient.invalidateQueries({ queryKey: ['tenants'] });
        setOutcome({ kind: 'checked', tenant });
      } catch (err) {
        setOutcome({ kind: 'refresh-failed', error: err });
      }
    })();
  }, [envReady, captured, wantEnv, envKnown, environmentId, navigate, isSingleEnvironment, queryClient]);

  const tenantId = captured.grant?.tenantId;
  const tenantLink = tenantId ? (
    <Link to={`/tenants/${encodeURIComponent(tenantId)}/overview`}>Open the tenant</Link>
  ) : (
    <Link to="/tenants">Go to tenants</Link>
  );

  return (
    <div className="flex max-w-[640px] flex-col gap-6">
      <h1 className="font-display text-3xl tracking-tight">Granting permissions</h1>

      {outcome.kind === 'working' ? (
        <div className="flex items-center gap-3">
          <Spinner size="tiny" />
          <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
            Checking the tenant&rsquo;s consent now.
          </span>
        </div>
      ) : null}

      {outcome.kind === 'checked' ? (
        outcome.tenant.consentStatus === 'consented' ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>Permissions granted</MessageBarTitle>
              {outcome.tenant.tenantDomain ?? outcome.tenant.tenantId} is ready for AirBridge to work
              in. {tenantLink}
            </MessageBarBody>
          </MessageBar>
        ) : (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Not showing as granted yet</MessageBarTitle>
              <span className="mr-2 inline-block align-middle">
                <ConsentStatusChip status={outcome.tenant.consentStatus} />
              </span>
              Entra can take a minute to report a new grant. Use Re-check on the tenant shortly; if it
              stays like this, the approval may not have been completed. {tenantLink}
            </MessageBarBody>
          </MessageBar>
        )
      ) : null}

      {outcome.kind === 'microsoft-error' ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>The permissions were not granted</MessageBarTitle>
            {outcome.message} {tenantLink}
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {outcome.kind === 'refresh-failed' ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Could not re-check the tenant</MessageBarTitle>
            {errorText(outcome.error)} The grant itself happened in Microsoft&rsquo;s pages and is not
            affected; use Re-check on the tenant. {tenantLink}
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {outcome.kind === 'unknown-tenant' ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Nothing to finish here</MessageBarTitle>
            This page could not tell which tenant the grant was for. Open the tenant and use Re-check.{' '}
            {tenantLink}
          </MessageBarBody>
        </MessageBar>
      ) : null}
    </div>
  );
}
